import { useState, useRef, useEffect } from "react";
import "./styles/App.css";
import SearchBar from "./components/SearchBar";
import FolderList from "./components/FolderList";
import Sidebar from "./components/Sidebar";
import EmailList from "./components/EmailList";
import AddToFavorites from "./components/AddToFavorites";
import SaveDrawer from "./components/SaveDrawer";
import ArchiveHistory from "./components/ArchiveHistory";
import SettingsView from "./components/SettingsView";
import { AppProvider, useAppContext } from "./contexts/AppContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";

function AppContent() {
    const {
        currentView,
        openedProject,
    } = useAppContext();

    const [contentWidth, setContentWidth] = useState(0);
    const [isCompactLayout, setIsCompactLayout] = useState(false);
    const contentRef = useRef<HTMLDivElement | null>(null);

    // Use hysteresis to prevent rapid switching between layouts
    useEffect(() => {
        if (contentWidth > 0) {
            if (!isCompactLayout && contentWidth < 480) {
                setIsCompactLayout(true);
            } else if (isCompactLayout && contentWidth > 580) {
                setIsCompactLayout(false);
            }
        }
    }, [contentWidth, isCompactLayout]);

    // Force non-compact layout for AddToFavorites view
    useEffect(() => {
        if (currentView === "addFavorites") {
            setContentWidth(800);
        }
    }, [currentView]);

    useEffect(() => {
        const node = contentRef.current;
        if (!node) {
            return;
        }

        const updateWidth = () => {
            const { width } = node.getBoundingClientRect();
            setContentWidth(width);
        };

        updateWidth();

        if (typeof window !== "undefined" && "ResizeObserver" in window) {
            const observer = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (entry) {
                    setContentWidth(entry.contentRect.width);
                }
            });

            observer.observe(node);
            return () => {
                observer.disconnect();
            };
        }

        if (typeof window !== "undefined") {
            const w = window as unknown as (Window & typeof globalThis);
            w.addEventListener("resize", updateWidth);
            return () => {
                w.removeEventListener("resize", updateWidth);
            };
        }

        return undefined;
    }, []);

    const { sizing } = useTheme();
    const appClassName = isCompactLayout ? `app app-compact app-${sizing}` : `app app-${sizing}`;

    return (
        <div className={appClassName}>
            <div className="main-content">
                <div className="content-area" ref={contentRef}>
                    {currentView === "project" && openedProject ? (
                        <div className="view-content">
                            <EmailList />
                        </div>
                    ) : currentView === "addFavorites" ? (
                        <div className="view-content">
                            <AddToFavorites />
                        </div>
                    ) : currentView === "save" ? (
                        <div className="view-content">
                            <SaveDrawer />
                        </div>
                    ) : currentView === "history" ? (
                        <div className="view-content">
                            <ArchiveHistory />
                        </div>
                    ) : currentView === "settings" ? (
                        <div className="view-content">
                            <SettingsView />
                        </div>
                    ) : (
                        <div className="view-content">
                            <div className="folders-pane">
                                <SearchBar />
                                <FolderList />
                            </div>
                        </div>
                    )}
                </div>
                <Sidebar />
            </div>
        </div>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AppProvider>
                <AppContent />
            </AppProvider>
        </ThemeProvider>
    );
}

export default App;
